import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, TrendingUp, Clock, AlertTriangle, CheckCircle, Calendar, User, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const DashboardEPI = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/demandes-epi/dashboard/stats');
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-8 w-8 text-blue-600" />
            Tableau de Bord HSE
          </h1>
          <p className="text-gray-600 mt-2">
            Vue d'ensemble des demandes HSE
          </p>
        </div>
        <Button onClick={() => navigate('/epi/demandes')} variant="outline" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Retour aux demandes
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Package className="h-8 w-8 mx-auto text-gray-600 mb-2" />
              <p className="text-sm text-gray-600">Total demandes</p>
              <p className="text-3xl font-bold">{stats?.stats?.total_demandes || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto text-orange-600 mb-2" />
              <p className="text-sm text-orange-600">En attente</p>
              <p className="text-3xl font-bold text-orange-600">{stats?.stats?.en_attente || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto text-blue-600 mb-2" />
              <p className="text-sm text-blue-600">En cours</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.stats?.en_cours || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
              <p className="text-sm text-green-600">Livrées</p>
              <p className="text-3xl font-bold text-green-600">{stats?.stats?.livrees || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 mx-auto text-red-600 mb-2" />
              <p className="text-sm text-red-600">Rejetées</p>
              <p className="text-3xl font-bold text-red-600">{stats?.stats?.rejetees || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Demandes à renouveler */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              Demandes à Renouveler (&lt; 30 jours)
            </CardTitle>
            <CardDescription>
              Demandes qui arrivent à expiration prochainement
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!stats?.epi_a_renouveler || stats.epi_a_renouveler.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">Aucune demande à renouveler</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.epi_a_renouveler.map((epi) => (
                  <Card
                    key={epi.id}
                    className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/epi/demandes/${epi.id}`)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <p className="font-medium">{epi.collaborateur_nom}</p>
                            <span className="text-sm text-gray-500">({epi.collaborateur_matricule})</span>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="h-4 w-4 text-gray-400" />
                            <p className="text-sm text-gray-600">{epi.type_epi_nom}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <p className="text-xs text-gray-500">
                              Expiration: {formatDate(epi.date_limite_remplacement)}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-orange-100 text-orange-800">
                          {epi.jours_restants} jour{epi.jours_restants > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Demandes expirées */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Demandes Expirées
            </CardTitle>
            <CardDescription>
              Demandes dont la date limite est dépassée
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!stats?.epi_expires || stats.epi_expires.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">Aucune demande expirée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.epi_expires.map((epi) => (
                  <Card
                    key={epi.id}
                    className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/epi/demandes/${epi.id}`)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <p className="font-medium">{epi.collaborateur_nom}</p>
                            <span className="text-sm text-gray-500">({epi.collaborateur_matricule})</span>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="h-4 w-4 text-gray-400" />
                            <p className="text-sm text-gray-600">{epi.type_epi_nom}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <p className="text-xs text-gray-500">
                              Expiré le: {formatDate(epi.date_limite_remplacement)}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-red-100 text-red-800">
                          Retard: {epi.jours_retard}j
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Répartition par type */}
      <Card>
        <CardHeader>
          <CardTitle>Répartition par Type de demande</CardTitle>
          <CardDescription>
            Nombre de demandes par type
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!stats?.repartition_types || stats.repartition_types.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune donnée disponible</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.repartition_types.map((item) => (
                <div key={item._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">{item.type_epi_nom || 'Type inconnu'}</span>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">{item.count} demande{item.count > 1 ? 's' : ''}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardEPI;